import { CommonModule } from '@angular/common';
import {
  Component,
  HostBinding,
  Input,
  OnInit,
  WritableSignal,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  ActionEnum,
  ActionPayloadDto,
} from '@app/core/dto/abstract-action.dto';
import { PopupPositionEnum } from '@app/core/enums/popup-view-type.enum';
import { BaseWidget } from '../base-widget/base-widget';
import { OrderCancellationConfirmationCardComponent } from '../popups/order-cancellation-confirmation-card/order-cancellation-confirmation-card.component';
import { ShipmentTrackerComponent } from '../shipment-tracker/shipment-tracker.component';
import { OrderState } from '@app/core/enums/order-state.enum';
import { StringUtils } from '@app/core/utils/string.utils';
import { NavigationOptions } from '@app/core/enums/navigation-options.enum';
@Component({
  selector: 'order-viewer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatExpansionModule,
    ShipmentTrackerComponent,
  ],
  templateUrl: './order-viewer.component.html',
  styleUrl: './order-viewer.component.scss',
})
export class OrderViewerComponent extends BaseWidget implements OnInit {
  @HostBinding('class') @Input() ngClass: any;
  @HostBinding('style') @Input() ngStyle: any;

  @Input() actionEvent: (event: any) => void;
  @Input() slot: any;
  data: any;
  orderItemDataMap: any;

  currentPage: string = '/orders/';
  openedShipmentTrackerId: WritableSignal<number> = signal(null);
  openedReturnTrackerId: WritableSignal<number> = signal(null);

  defaultOpenedOrderId: number | null = null;
  OrderState = OrderState;
  StringUtils = StringUtils;
  OrderNoPrefix: number = 10;

  constructor(public dialog: MatDialog) {
    super();
  }

  ngOnInit(): void {
    this.data = this.slot.widget.data;
    this.orderItemDataMap = this.data.orderItemDataMap;
  }

  calculateOrderItemDiscount(orderItem: any) {
    return (
      Number(orderItem.bulk_discount || 0) +
      Number(orderItem.emi_discount || 0) +
      Number(orderItem.promo_discount || 0)
    );
  }

  getOrderDiscount(orderItem: any) {
    return (
      Number(orderItem.bulk_discount) +
      Number(orderItem.promo_discount) +
      Number(orderItem.emi_discount)
    );
  }

  startOrderItemCancellation(orderItemId: string) {
    const cancellationDialog = this.dialog.open(
      OrderCancellationConfirmationCardComponent,
      {
        data: {
          orderItemId: orderItemId,
          cancellationReasons: this.data.cancellationReasons || [],
        },
      },
    );

    cancellationDialog
      .afterClosed()
      .subscribe((cancelOrderItemPayload = {}) => {
        if (
          !cancelOrderItemPayload.orderItemId ||
          !cancelOrderItemPayload.reason
        ) {
          return;
        }

        this.cancelOrderItem(cancelOrderItemPayload);
      });
  }

  returnCancelDialog(returnDialog: any, returnId: string) {
    const popupData: ActionPayloadDto = returnDialog.action?.params;
    const viewType: PopupPositionEnum = returnDialog.value.viewType;
    if (returnDialog.value?.confirmationBtn?.action) {
      returnDialog.value.confirmationBtn.action = {
        ...returnDialog.value.confirmationBtn.action,
        params: { returnId: returnId },
      };
    }
    const dynamicPopupData = {
      data: {
        slot: returnDialog.value,
        onAction: this.onActionCall.bind(this),
        viewType: returnDialog.viewType,
      },
    };
    this.showPopup(dynamicPopupData, popupData.popupName, viewType);
  }

  cancelOrderItem(cancelOrderItemPayload: any) {
    this.actionEvent({
      actionData: {
        type: ActionEnum.CANCEL_ORDER,
        params: cancelOrderItemPayload,
      },
    });
  }

  openShipmentTracker(orderItemId: number = null) {
    if (this.openedShipmentTrackerId() === orderItemId) {
      orderItemId = null;
    }

    this.openedShipmentTrackerId.set(orderItemId);
  }

  openReturnTracker(orderItemId: number = null) {
    if (this.openedReturnTrackerId() === orderItemId) {
      orderItemId = null;
    }

    this.openedReturnTrackerId.set(orderItemId);
  }

  initiateReturn(orderId: number) {
    const url: string = this.data.urls.itemReturnUrl + '/' + orderId;
    this.actionEvent({
      actionData: {
        type: ActionEnum.NAVIGATION_ROUTER,
        params: {
          url,
        },
      },
    });
  }

  initiateRating(sku: string) {
    const url: string = StringUtils.format('/user/write-review/{0}', sku);
    this.actionEvent({
      actionData: {
        type: ActionEnum.EXTERNAL_NAVIGATION,
        params: {
          url,
          navigationOption: NavigationOptions.SELF,
        },
      },
    });
  }

  getReviewerData(reviewerData = {}) {
    return {
      ...reviewerData,
      images: this.data?.reviewerStaticInfo?.images || {},
      text: this.data?.reviewerStaticInfo?.text || {},
      settings: this.data?.reviewerStaticInfo?.settings || {},
      validations: this.data?.reviewerStaticInfo?.validations || {},
      action:
        this.data?.reviewerStaticInfo?.reviewFormDialogData?.value?.submitReview
          ?.action || {},
    };
  }

  openReviewForm(reviewerData) {
    const popupData: ActionPayloadDto =
      this.data?.reviewerStaticInfo?.reviewFormDialogData.action?.params;
    const viewType: PopupPositionEnum =
      this.data?.reviewerStaticInfo?.reviewFormDialogData.value.viewType;
    const dynamicPopupData = {
      data: {
        slot: this.getReviewerData(reviewerData),
        onAction: this.onActionCall.bind(this),
        viewType: this.data?.reviewerStaticInfo?.reviewFormDialogData.viewType,
      },
    };
    this.showPopup(dynamicPopupData, popupData.popupName, viewType);
  }

  onActionCall(action: any) {
    this.actionEvent({
      actionData: { ...action },
    });
  }

  getStatusStyle(status: OrderState): string {
    let backgroundColor: string;

    switch (status) {
      case OrderState.CONFIRMED:
        backgroundColor = '#A7D3FF';
        break;
      case OrderState.DELIVERED:
        backgroundColor = '#C8FCAE';
        break;
      case OrderState.REJECTED:
      case OrderState.CANCELLED:
        backgroundColor = '#FFB3B3';
        break;
      case OrderState.BOOKED:
        backgroundColor = '#FFF9C4';
        break;
      case OrderState.CART:
        backgroundColor = '#FFE0B2';
        break;
      case OrderState.GUEST_CART:
        backgroundColor = '#D1C4E9';
        break;
      case OrderState.TEMPORARY_CART:
        backgroundColor = '#BBDEFB';
        break;
      case OrderState.CURRENT_QUOTATION:
        backgroundColor = '#C5CAE9';
        break;
      case OrderState.SAVED_QUOTATION:
        backgroundColor = '#FFCCBC';
        break;
      case OrderState.PURCHASED_QUOTATION:
        backgroundColor = '#C8E6C9';
        break;
      case OrderState.RETURNED:
        backgroundColor = '#FFEBEE';
        break;
      case OrderState.PARTIAL_SHIPPED:
        backgroundColor = '#F0F4C3';
        break;
      case OrderState.SHIPPED:
        backgroundColor = '#E1F5FE';
        break;
      case OrderState.PAYMENT_INITIATED:
        backgroundColor = '#FFF59D';
        break;
      case OrderState.FAILED:
        backgroundColor = '#FFCDD2';
        break;
      default:
        backgroundColor = '#E0E0E0'; // Default color
    }

    // Return the combined styles including padding
    return `background-color: ${backgroundColor}; color: #000000; padding: 4px 12px;`;
  }
}
